import { FC, useState, useEffect, useMemo } from 'react'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'

import LectorBio from 'components/search/lector-single/lector-bio/lector-bio'
import LectorSingleSearch from 'components/search/lector-single/lector-single-main/lector-single-main'
import SEOMetatags from 'components/parts/seo-metatags/seo-metatags'
import SEOBreadCrumbs from 'components/parts/seo-metatags/seo-breadcrumbs'
import LectorCourses from 'components/search/lector-single/lector-courses/lector-courses'
import SearchFilterContext from 'context/searchFilterContext'
import Auth from 'store/auth'
const AdminEditBtn = dynamic(() => import('components/parts/admin-edit-btn/admin-edit-btn'))
import SEOContext from 'context/SEOContext'

import { getDigitFromString, linkWithTranslation } from 'utils/getURLData'
import restRequest from 'utils/server-side/restRequest'
import { LectorBC } from 'constants/SEO-breadcrumbs'
import { go404 } from 'utils/redirect'
import { ILectorCard } from 'constants/types/card.types'
import { Pathname } from 'constants/data'
import { FilterIndex } from 'constants/search-data/search-data'
import { URL } from 'constants/SEO'
import { Tag } from 'constants/tags'
import { getDoctorForm } from 'utils/formatSpeciality'
import { capitalizeFirstLetter } from 'utils/captitalizeFirstLetter'
import { PLACEFLAG_LECTOR_PERSONAL } from 'constants/analytic-data'

interface ISingleLector {
  lectorData: ILectorCard
}

const DEFAULT_TITLE = 'Материалы лектора'

const SingleLectorPage: FC<ISingleLector> = ({ lectorData }: ISingleLector) => {
  const router = useRouter()
  const { isAdmin } = Auth.useAuth()
  const lector: ILectorCard = lectorData

  const [filterState, setFilterState] = useState(FilterIndex.TURN_OFF_INDEX)

  const setFilter = (filter?: number) => {
    if (!filterState) return setFilterState(FilterIndex.TURN_ON_INDEX)
    if (typeof filter === 'number') return setFilterState(Number(filter))
    if (filterState > FilterIndex.TURN_ON_INDEX) return setFilterState(FilterIndex.TURN_ON_INDEX)
    return setFilterState(FilterIndex.TURN_OFF_INDEX)
  }

  const providerValues = { filterState, setFilter }
  const FullName = `${lector.lastName} ${lector.firstName} ${lector.secondName}`
  const doctorForm = getDoctorForm(lector.Specialities?.[0]?.name || '')

  const SEOData = {
    TITLE: FullName ? `${FullName} ${doctorForm}` : '',
    DESCRIPTION:
      lector.regalia && FullName
        ? `${capitalizeFirstLetter(doctorForm)} ${FullName} - ${lector.regalia}`
        : '',
    LINK: URL + '/' + Pathname.LECTURE + '/' + lector.linkTranslation,
    PLACE: PLACEFLAG_LECTOR_PERSONAL,
  }

  const SEOLectorBC = useMemo(
    () =>
      LectorBC({
        name: SEOData.TITLE,
        id: lector.linkTranslation || '',
      }),
    [lector.id]
  )

  const lastName = lector.lastName || ''
  const secondNames = [lector.firstName, lector.secondName]
    .filter((item) => item)
    .map((item) => item?.length && item[0])
    .join('. ')

  const isHidden = lector?.tags?.includes(Tag.hidden)

  const pageTitle = `${DEFAULT_TITLE}: ${lastName}&nbsp;${secondNames}.`

  useEffect(() => {
    if (lector.linkTranslation) {
      const preparedLink = linkWithTranslation(router.asPath, lector.linkTranslation)
      router.replace(preparedLink, undefined, { shallow: true })
    }
  }, [lectorData])

  return (
    <>
      <SEOMetatags data={SEOData} nofollow={isHidden} />
      <SEOBreadCrumbs data={SEOLectorBC} />

      <SearchFilterContext.Provider value={providerValues}>
        <SEOContext.Provider value={SEOData}>
          <LectorSingleSearch lectorData={lector} pageTitle={lector?.customTitle || pageTitle}>
            {lector.id && !isHidden ? (
              <>
                <LectorBio {...lector} />
                <LectorCourses lectorId={lector.id} />
              </>
            ) : (
              <></>
            )}
          </LectorSingleSearch>
        </SEOContext.Provider>
      </SearchFilterContext.Provider>

      {isAdmin && <AdminEditBtn path="createLectur" />}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  let lectorData = null

  if (context?.params?.id) {
    try {
      const lectorId = getDigitFromString(context.params.id as string)
      const restRet = await restRequest(context, 'get', `/api/lectur/${lectorId}`, {})
      lectorData = restRet?.data

      if (!lectorData) return go404()
      if (context.params.id !== lectorData.linkTranslation)
        return {
          redirect: {
            destination: `/${Pathname.LECTURE}/${lectorData.linkTranslation}`,
            permanent: true,
          },
        }
    } catch (err) {
      console.warn(err)
      return go404()
    }
  }

  return {
    props: {
      lectorData,
    },
  }
}

export default SingleLectorPage
